
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DateRangePicker from '../components/DateRangePicker';
import InspectorHoverCard from '../components/InspectorHoverCard';

interface ContainerInspectionRecord {
  id: string;
  containerNumber: string;
  shippingLine: string;
  customerName: string;
  vehicleNo: string;
  qaExecutive: {
    name: string;
    initials: string;
    color: string;
    photo?: string;
    inspectorId: string | null;
  };
  date: string;
  status: 'In Progress' | 'Completed' | 'Pending';
}

// Mock data removed in favor of real API data


const ContainerInspectionReports: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState('All');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reports, setReports] = useState<ContainerInspectionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;


  useEffect(() => {
    const fetchReports = async () => {
      try {
        setIsLoading(true);
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/container-inspections`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.status === 401) {
          logout();
          return;
        }

        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Failed to fetch reports');

        const formatted = result.data.map((r: any) => ({
          id: r.id,
          containerNumber: r.container_no || 'N/A',
          shippingLine: r.shipping_line || 'N/A',
          customerName: r.invoice_no || 'N/A', // Using invoice_no as placeholder for customer
          vehicleNo: r.vehicle_no || 'N/A',
          qaExecutive: {
            name: r.inspector?.name || r.inspector_name || 'Unknown',
            initials: getInitials(r.inspector?.name || r.inspector_name || 'Unknown'),
            color: getAvatarColor(r.inspector?.id || 'default'),
            photo: r.inspector?.profileImage || undefined,
            inspectorId: r.inspector?.id || null,
          },
          date: new Date(r.created_at).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }),
          status: r.status === 'IN_PROGRESS' ? 'In Progress' : r.status === 'COMPLETED' ? 'Completed' : 'Pending',
        }));

        setReports(formatted);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) fetchReports();
  }, [token, logout]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter, selectedUser, dateFrom, dateTo]);


  type UserObj = { name: string; photo?: string; color: string; initials: string; inspectorId?: string | null };
  const uniqueUserObjects: UserObj[] = [
    { name: 'All', photo: undefined, color: '', initials: '' },
    ...(Array.from(new Map(reports.map((r: ContainerInspectionRecord) => [r.qaExecutive.name, r.qaExecutive])).values()) as UserObj[]),
  ];

  const filteredReports = reports.filter(r => {
    const matchesStatus = activeFilter === 'All' || r.status === activeFilter;
    const matchesUser = selectedUser === 'All' || r.qaExecutive.name === selectedUser;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      r.containerNumber.toLowerCase().includes(q) ||
      r.customerName.toLowerCase().includes(q) ||
      r.vehicleNo.toLowerCase().includes(q) ||
      r.shippingLine.toLowerCase().includes(q) ||
      r.qaExecutive.name.toLowerCase().includes(q);
    // Parse the report date (formatted as "11 Mar 2026") back to a comparable date
    const reportDate = new Date(r.date);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    const matchesDate = (!from || reportDate >= from) && (!to || reportDate <= to);
    return matchesStatus && matchesUser && matchesSearch && matchesDate;
  });


  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedReports = filteredReports.slice(indexOfFirstItem, indexOfLastItem);


  // Status Badge Logic... (rest of component)

  const getStatusBadge = (status: ContainerInspectionRecord['status']) => {
    switch (status) {
      case 'In Progress':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blue-700 ring-1 ring-inset ring-blue-600/20 uppercase tracking-wide">
            <span className="h-1 w-1 rounded-full bg-blue-600"></span> In Progress
          </span>
        );
      case 'Completed':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-600/20 uppercase tracking-wide">
            <span className="h-1 w-1 rounded-full bg-emerald-600"></span> Completed
          </span>
        );
      case 'Pending':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-700 ring-1 ring-inset ring-amber-600/20 uppercase tracking-wide">
            <span className="h-1 w-1 rounded-full bg-amber-600"></span> Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-[10px] font-bold text-slate-700 ring-1 ring-inset ring-slate-600/20 uppercase tracking-wide">
            <span className="h-1 w-1 rounded-full bg-slate-600"></span> {status}
          </span>
        );
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = [
      'bg-blue-100 text-blue-600',
      'bg-emerald-100 text-emerald-600',
      'bg-purple-100 text-purple-600',
      'bg-amber-100 text-amber-600',
      'bg-rose-100 text-rose-600',
      'bg-indigo-100 text-indigo-600',
      'bg-slate-200 text-slate-600'
    ];
    const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Container Inspection Report</h1>
        <p className="text-sm text-slate-500">Manage container loading inspection reports and outcomes</p>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center">
          {/* Search Box */}
          <div className="relative w-full md:max-w-sm">
            <input
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm placeholder-slate-400 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] outline-none"
              placeholder="Search by Container, Customer, Vehicle..."
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-[20px] text-slate-400">search</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            )}
          </div>

          {/* User Filter */}
          <div className="relative inline-block" ref={userDropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen((prev: boolean) => !prev)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer min-w-[140px]"
            >
              {selectedUser === 'All' ? (
                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                </span>
              ) : (() => {
                const u = uniqueUserObjects.find(x => x.name === selectedUser);
                return u?.photo ? (
                  <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                ) : (
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u?.color || 'bg-slate-200 text-slate-600'}`}>
                    {u?.initials || selectedUser[0]}
                  </span>
                );
              })()}
              <span className="truncate">{selectedUser === 'All' ? 'All Users' : selectedUser}</span>
              <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
            </button>
            {isUserDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 z-20 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                {uniqueUserObjects.map(u => (
                  <button
                    key={u.name}
                    onClick={() => { setSelectedUser(u.name); setIsUserDropdownOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedUser === u.name ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                  >
                    {u.name === 'All' ? (
                      <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                      </span>
                    ) : u.photo ? (
                      <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                    ) : (
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u.color}`}>
                        {u.initials}
                      </span>
                    )}
                    {u.name === 'All' ? 'All Users' : u.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-medium text-slate-700">Filter Date:</span>
          <DateRangePicker 
            range={{ from: dateFrom, to: dateTo }}
            onChange={(range) => {
              setDateFrom(range.from);
              setDateTo(range.to);
            }}
            onClear={() => {
              setDateFrom('');
              setDateTo('');
            }}
          />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {['All', 'Completed', 'In Progress', 'Pending'].map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all shadow-sm ${activeFilter === filter
              ? 'bg-[#136bce] text-white shadow-blue-500/30'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
          >
            {filter}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase text-slate-500 font-bold">
              <tr>
                <th className="px-6 py-4 tracking-wider border-b border-slate-200">Container Number</th>
                <th className="px-6 py-4 tracking-wider border-b border-slate-200">Shipping Line</th>
                <th className="px-6 py-4 tracking-wider border-b border-slate-200">Vehicle / Invoice</th>
                <th className="px-6 py-4 tracking-wider border-b border-slate-200">QA Executive</th>
                <th className="px-6 py-4 tracking-wider border-b border-slate-200">Inspection Date</th>
                <th className="px-6 py-4 tracking-wider border-b border-slate-200">Status</th>
                <th className="px-6 py-4 border-b border-slate-200 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 mr-2 align-middle"></div>
                    Loading reports...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400 font-medium whitespace-nowrap">
                    No reports found matching the criteria.
                  </td>
                </tr>
              ) : (
                paginatedReports.map((report) => (
                  <tr
                    key={report.id}
                    className="group transition-colors hover:bg-blue-50/30 cursor-pointer"
                    onClick={() => navigate(`/container-inspection-report/${report.id}`)}
                  >

                    <td className="px-6 py-4 align-middle text-slate-900 font-semibold whitespace-nowrap text-xs">{report.containerNumber}</td>
                    <td className="px-6 py-4 align-middle text-slate-700 whitespace-nowrap font-medium">{report.shippingLine}</td>
                    <td className="px-6 py-4 align-middle text-slate-700 whitespace-nowrap font-medium text-xs">
                      <div className="flex flex-col">
                        <span>{report.vehicleNo}</span>
                        <span className="text-[10px] text-slate-400 uppercase font-bold">{report.customerName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-700 whitespace-nowrap">
                      <InspectorHoverCard
                        inspectorId={report.qaExecutive.inspectorId}
                        name={report.qaExecutive.name}
                        initials={report.qaExecutive.initials}
                        color={report.qaExecutive.color}
                        photo={report.qaExecutive.photo}
                        token={token}
                      >
                        {report.qaExecutive.photo ? (
                          <img
                            src={report.qaExecutive.photo}
                            alt={report.qaExecutive.name}
                            className="h-7 w-7 rounded-full object-cover border border-slate-200"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const sibling = target.nextElementSibling as HTMLElement;
                              if (sibling) sibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold ${report.qaExecutive.color}`}
                          style={{ display: report.qaExecutive.photo ? 'none' : 'flex' }}
                        >
                          {report.qaExecutive.initials}
                        </div>
                        {report.qaExecutive.name}
                      </InspectorHoverCard>
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-500 whitespace-nowrap font-medium">{report.date}</td>
                    <td className="px-6 py-4 align-middle whitespace-nowrap">{getStatusBadge(report.status)}</td>
                    <td className="px-6 py-4 align-middle text-right">
                      <span className="material-symbols-outlined text-slate-300 group-hover:text-[#136bce] transition-colors">chevron_right</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-sm text-slate-500 font-medium">
            Showing <span className="font-semibold">{indexOfFirstItem + 1}</span> to <span className="font-semibold">{Math.min(indexOfLastItem, filteredReports.length)}</span> of <span className="font-semibold">{filteredReports.length}</span> results
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show first, last, and pages around current
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => (
                <React.Fragment key={page}>
                  {index > 0 && array[index - 1] !== page - 1 && (
                    <div className="flex h-8 w-8 items-center justify-center text-slate-400 font-bold">...</div>
                  )}
                  <button
                    onClick={() => setCurrentPage(page)}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold transition-colors ${
                      currentPage === page
                        ? "bg-[#136bce] text-white shadow-sm"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {page}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContainerInspectionReports;
